import { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { Filter, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export default function SearchFilters({ onFilterChange, resultsCount }) {
  const { t } = useTranslation();
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    skill: '',
    location: '',
    minRating: 0,
    trustLevel: '',
    availability: '',
    sortBy: 'rating'
  });

  const handleFilterChange = (key, value) => {
    // Convert "all" to empty string for API
    const actualValue = value === 'all' ? '' : value;
    const newFilters = { ...filters, [key]: actualValue };
    setFilters(newFilters);
    onFilterChange(newFilters);
  };

  const clearFilters = () => {
    const emptyFilters = {
      skill: '',
      location: '',
      minRating: 0,
      trustLevel: '',
      availability: '',
      sortBy: 'rating'
    };
    setFilters(emptyFilters);
    onFilterChange(emptyFilters);
  };

  const hasActiveFilters = filters.skill || filters.location || filters.minRating > 0 || 
                          filters.trustLevel || filters.availability;

  return (
    <Card className="p-4 bg-gradient-to-r from-teal-50 to-cyan-50 border-teal-200">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowFilters(!showFilters)}
            className="gap-2"
          >
            <Filter className="h-4 w-4" />
            {t('search.filters')} {hasActiveFilters && `(${Object.values(filters).filter(v => v && v !== 'rating').length})`}
          </Button>
          {hasActiveFilters && (
            <Button
              variant="ghost"
              size="sm"
              onClick={clearFilters}
              className="text-red-500 hover:text-red-600"
            >
              <X className="h-4 w-4 mr-1" />
              {t('search.clearFilters')}
            </Button>
          )}
        </div>
        {resultsCount !== undefined && (
          <span className="text-sm text-gray-600">
            {resultsCount} {t('search.resultsFound')}
          </span>
        )}
      </div>

      {showFilters && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 pt-4 border-t border-teal-200">
          {/* Skill Filter */}
          <div>
            <label className="text-sm font-medium mb-2 block">{t('search.skillFilter')}</label>
            <Input
              placeholder={t('search.skillPlaceholder')}
              value={filters.skill}
              onChange={(e) => handleFilterChange('skill', e.target.value)}
              className="bg-white"
            />
          </div>

          {/* Location Filter */}
          <div>
            <label className="text-sm font-medium mb-2 block">{t('search.locationFilter')}</label>
            <Input
              placeholder={t('search.locationPlaceholder')}
              value={filters.location}
              onChange={(e) => handleFilterChange('location', e.target.value)}
              className="bg-white"
            />
          </div>

          {/* Trust Level Filter */}
          <div>
            <label className="text-sm font-medium mb-2 block">{t('search.trustLevelFilter')}</label>
            <Select value={filters.trustLevel} onValueChange={(val) => handleFilterChange('trustLevel', val)}>
              <SelectTrigger className="bg-white">
                <SelectValue placeholder={t('search.allLevels')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('search.allLevels')}</SelectItem>
                <SelectItem value="newcomer">{t('search.newcomer')}</SelectItem>
                <SelectItem value="trusted">{t('search.trusted')}</SelectItem>
                <SelectItem value="expert">{t('search.expert')}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Availability Filter */}
          <div>
            <label className="text-sm font-medium mb-2 block">{t('search.availabilityFilter')}</label>
            <Input
              placeholder={t('search.availabilityPlaceholder')}
              value={filters.availability}
              onChange={(e) => handleFilterChange('availability', e.target.value)}
              className="bg-white"
            />
          </div>

          {/* Sort By */}
          <div>
            <label className="text-sm font-medium mb-2 block">{t('search.sortBy')}</label>
            <Select value={filters.sortBy} onValueChange={(val) => handleFilterChange('sortBy', val)}>
              <SelectTrigger className="bg-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="rating">{t('search.highestRating')}</SelectItem>
                <SelectItem value="reviews">{t('search.mostReviews')}</SelectItem>
                <SelectItem value="credits">{t('search.mostCredits')}</SelectItem>
                <SelectItem value="name">{t('search.nameAZ')}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Minimum Rating Slider */}
          <div>
            <label className="text-sm font-medium mb-2 block">
              {t('search.minRating')}: {(filters.minRating || 0).toFixed(1)} ⭐
            </label>
            <Slider
              value={[filters.minRating]}
              onValueChange={([val]) => handleFilterChange('minRating', val)}
              max={5}
              step={0.5}
              className="mt-2"
            />
          </div>
        </div>
      )}
    </Card>
  );
}
